import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar } from "lucide-react";
import PromoCountdown from "@/components/PromoCountdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  actualite: "Actualité",
  promotion: "Promotion",
  avantage: "Avantage",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function NewsPreview() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/articles?limit=3`)
      .then((res) => setItems(res.data.slice(0, 3)))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section
      id="actualites-preview"
      className="relative py-24 md:py-32 bg-white border-y border-[#0a192f]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#c05c3d]" />
              <span className="overline">Journal du cabinet</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#0a192f]">
              Dernières actualités<br />
              <span className="italic">& avantages clients.</span>
            </h2>
          </div>
          <Link
            to="/actualites"
            className="group inline-flex items-center gap-2 border border-[#0a192f]/70 text-[#0a192f] px-6 py-3 text-sm tracking-wide hover:bg-[#0a192f] hover:text-[#efeade] transition-colors"
          >
            Voir toutes les actualités
            <ArrowUpRight
              size={16}
              strokeWidth={1.5}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((a) => {
            const badgeColor =
              a.category === "promotion"
                ? "bg-[#c05c3d] text-[#efeade]"
                : a.category === "avantage"
                ? "bg-[#b08d57] text-[#0a192f]"
                : "bg-[#0a192f] text-[#efeade]";
            return (
              <Link
                key={a.id}
                to={`/actualites/${a.slug}`}
                className="group flex flex-col border border-[#0a192f]/10 bg-white hover:-translate-y-1 transition-transform duration-500"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={a.image_url}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span
                    className={`absolute top-4 left-4 text-[10px] tracking-[0.24em] uppercase font-semibold px-3 py-1.5 ${badgeColor}`}
                  >
                    {CATEGORY_LABEL[a.category] || a.category}
                  </span>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="overline mb-3">{a.tag}</div>
                  <h3 className="font-serif text-xl leading-tight text-[#0a192f] group-hover:text-[#c05c3d] transition-colors">
                    {a.title}
                  </h3>
                  {a.category === "promotion" && a.promo_ends_at && (
                    <div className="mt-3">
                      <PromoCountdown endsAt={a.promo_ends_at} />
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t border-[#0a192f]/10 text-xs text-[#475569] flex items-center gap-2">
                    <Calendar size={12} strokeWidth={1.5} />
                    {formatDate(a.published_at)}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
